import { App as CapacitorApp } from '@capacitor/app';
import { useEffect } from 'react';

import { Bogen, BogenFooter } from '../features/bogen/Bogen';
import { CaptureOverlay } from '../features/bogen/CaptureOverlay';
import { Header } from '../features/header/Header';
import { SimulativBand } from '../features/bogen/SimulativBand';
import { CommentSheet } from '../features/comment/CommentSheet';
import { CandidatesSheet } from '../features/sheets/CandidatesSheet';
import { CriterionDialog } from '../features/sheets/CriterionDialog';
import { ScoreDialog } from '../features/sheets/ScoreDialog';
import { SettingsDialog } from '../features/sheets/SettingsDialog';
import { SperrDialog } from '../features/sheets/SperrDialog';
import { istGesperrt } from '../features/sheets/einstellungenSperre';
import { SetupSheet } from '../features/sheets/SetupSheet';
import { CandidatePickDialog } from '../features/start/CandidatePickDialog';
import { ClockWarning } from '../features/start/ClockWarning';
import { ResumeDialog } from '../features/start/ResumeDialog';
import { Schlafscheibe } from '../features/schlaf/Schlafscheibe';
import { BeendenDialog } from '../features/beenden/BeendenDialog';
import { SwitchDialog } from '../features/sheets/SwitchDialog';
import { TimerDialog } from '../features/sheets/TimerDialog';
import { LockedDialog } from '../features/submit/LockedDialog';
import { ScannerScreen } from '../features/submit/ScannerScreen';
import { SubmitDialog } from '../features/submit/SubmitDialog';
import { VignetteDialog } from '../features/sheets/VignetteDialog';
import { useLocaleStore, useT } from '../i18n';
import { aufBeendenwunsch, beschrifteMenue } from '../platform/beenden';
import { halteWach } from '../platform/wach';
import {
  meldeRueckstand,
  meldeUebertragungLaeuft,
  meldeUebertragungstext,
} from '../features/header/rueckstandMelden';
import { starteClassicTakt } from '../server/classic/takt';
import { stimmeSpracheAb } from '../features/sprache';
import { markiereMeldungFaellig } from '../server/geraeteEinstellungen';
import { meldeSchlaf, starteRelayTakt } from '../server/takt';
import {
  selectStation, selectTasks, useAppStore } from '../store/useAppStore';
import { setzeQuerformat } from '../platform/ausrichtung';
import { AufsichtMitteilung } from '../features/aufsicht/AufsichtMitteilung';
import { stationLaeuft } from '../domain/stationLaeuft';
import { mitteilungEingegangen } from '../platform/haptics';
import { meldeUnbekannte } from '../platform/unbekannteEinstellungen';
import { useAufsichtStore } from '../store/useAufsichtStore';
import { useTimerStore } from '../store/useTimerStore';
import { setzeAppModus } from '../platform/appZustand';
import { applyTheme, buildTheme } from '../theme/theme';
import { Toast, useToastStore } from '../ui/Toast';

export function App() {
  const initLocale = useLocaleStore((s) => s.init);
  const dark = useAppStore((s) => s.dark);
  const accent = useAppStore((s) => s.accent);
  const switchTarget = useAppStore((s) => s.switchTarget);
  const startup = useAppStore((s) => s.startup);
  const sheet = useAppStore((s) => s.sheet);
  const clientId = useAppStore((s) => s.clientId);
  const t = useT();

  // Sprache einmal beim Start bestimmen: gespeicherte Wahl, sonst die des iPads.
  useEffect(() => {
    void initLocale();
  }, [initLocale]);

  // Querformat erlauben oder sperren (`landscape_mode`) — beim Start und bei jeder Änderung,
  // auch wenn sie per QR-Code oder Relays `SET_SETTINGS` kommt.
  const querformat = useAppStore((s) => s.querformat);
  useEffect(() => {
    void setzeQuerformat(querformat);
  }, [querformat]);

  /*
   * Danach bei jeder Änderung abstimmen: `language`, `language_changeable` und ob eine Prüfung
   * läuft — die Regel steht in `features/sprache.ts`. **Eine** Stelle für Start, gescannten
   * Code, Relays `SET_SETTINGS` und das Ende einer Prüfung.
   */
  const sprache = useAppStore((s) => s.sprache);
  const spracheWaehlbar = useAppStore((s) => s.spracheWaehlbar);
  const spracheGeladen = useLocaleStore((s) => s.geladen);
  const spracheGewaehlt = useLocaleStore((s) => s.chosen);
  // Das Ende einer Prüfung ist die Rückkehr zur Prüfungsauswahl (Entscheidung des Nutzers).
  const pruefungLaeuft = startup.kind !== 'auswahl';
  useEffect(() => {
    void stimmeSpracheAb({ sprache, waehlbar: spracheWaehlbar, pruefungLaeuft });
  }, [sprache, spracheWaehlbar, pruefungLaeuft, spracheGeladen, spracheGewaehlt]);

  /*
   * Der Classic-Takt: alle 10 s ein `POST /rest/log`, solange dieser Modus eingestellt ist.
   *
   * ⚠️ Er hängt **nicht** am Bogen, sondern an der laufenden App. Der Rumpf ist zugleich der
   * Herzschlag, an dem die Konsole des Prüfungsleiters das Gerät sieht — taktete nur der
   * Bogen, verschwände das iPad aus seiner Liste, sobald der Prüfer zwischen zwei Prüflingen
   * wartet. Ob überhaupt gesendet wird, entscheidet der Takt selbst anhand des Modus.
   */
  /*
   * Dazu der Relay-Takt: alle 30 s gehen erst die offenen `ExamLog`-Einträge, dann die
   * Dateien hinaus (`server/takt.ts`).
   *
   * ⚠️ **Beide werden angeworfen, nur einer arbeitet.** Jeder Takt prüft seinen Modus selbst;
   * eine Fallunterscheidung hier hiesse, sie nach jeder Umstellung in den Einstellungen von
   * Hand neu zu starten.
   */
  useEffect(() => {
    if (!clientId) return;
    /*
     * Der Melder trägt den Stand in die Kopfzeile (`features/header/Rueckstandspille.tsx`).
     * Beide Takte bekommen denselben — es arbeitet ohnehin immer nur einer, und die Pille
     * soll nicht wissen müssen, welcher.
     */
    const melder = {
      laeuft: meldeUebertragungLaeuft,
      zaehle: meldeRueckstand,
      text: meldeUebertragungstext,
    };
    // Einmal gleich zählen: bis zum ersten Takt vergehen sonst bis zu dreissig Sekunden, in
    // denen die Pille null zeigte, obwohl vom letzten Prüfungstag noch etwas liegt.
    void meldeRueckstand();
    const stoppClassic = starteClassicTakt({ clientId, melder });
    const stoppRelay = starteRelayTakt({
      clientId,
      melder,
      // Was Relay fernsetzt, gilt sofort — dieselbe Übernahme wie nach einem gescannten Code
      // (`SetupSheet.tsx::uebernehmen`).
      uebernehmeEinstellungen: (g) => useAppStore.getState().uebernehmeEinstellungen(g),
      fotoAufgenommen: () => useToastStore.getState().zeigeAufnahme(),
      befehle: {
        // Eine Prüfung ist im Gang, sobald ein Prüfling gewählt wird — bis dahin darf Relay löschen.
        bogenOffen: () => {
          const art = useAppStore.getState().startup.kind;
          return art === 'pruefling' || art === 'bereit' || art === 'wiedereinstieg';
        },
        geloescht: (ids) => ids.forEach((id) => useAppStore.getState().forgetExam(id)),
        demoEingelesen: () => useToastStore.getState().show('demoInstalled'),
        // `TIME_ADD` gibt nur einer laufenden Station Zeit; nach der Abgabe ist es zu spät
        // (Entscheidung des Nutzers, 2026-10-07).
        // Dazu ein kurzer Hinweis ohne Bestätigung (Entscheidung des Nutzers, 2026-10-08).
        zeitZugabe: (sekunden) => {
          if (!stationLaeuft(useAppStore.getState())) return 'steht';
          const wie = useTimerStore.getState().verlaengere(sekunden);
          // Bei ausgeblendeter Stationsuhr kein Hinweis — der Prüfer soll von der Zeit gar
          // nichts merken (Entscheidung des Nutzers, 2026-10-10).
          if (useAppStore.getState().timerShow) useToastStore.getState().zeigeZugabe(sekunden);
          return wie;
        },
        zeigeMitteilung: (id, text) => {
          if (!useAufsichtStore.getState().melde(id, text)) return;
          // Neu: wecken und klopfen (Entscheidung des Nutzers, 2026-10-07) — sonst läge die
          // Mitteilung unter der Schlafscheibe, und der Prüfer sähe sie erst beim nächsten Tipp.
          useAppStore.getState().setSchlaeft(false);
          mitteilungEingegangen();
        },
        mitteilungBestaetigt: (id) => useAufsichtStore.getState().bestaetigt.includes(id),
        // Unbekannte Namen sichtbar machen statt still übergehen (Bitte des Nutzers, 2026-10-08).
        unbekannteEinstellungen: (namen) => {
          meldeUnbekannte('Relay-Befehl', namen);
          useToastStore.getState().zeigeUnbekannte(namen);
        },
      },
    });
    /*
     * Beim Zurückkehren in den Vordergrund die Einstellungen neu melden (Relay-Spec §2). Was
     * das MDM in der Zwischenzeit geändert hat, geht an keinem Schreibweg der App vorbei —
     * diese Meldung ist der einzige Weg, auf dem Relay davon erfährt.
     */
    const resume = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      // Relays Geräteliste: Hintergrund/Sperre heisst `SLEEPING` (einmal gemeldet), zurück im
      // Vordergrund `ACTIVE` — die fällige Meldung weckt den Takt, der fragt dann gleich.
      setzeAppModus(isActive ? 'ACTIVE' : 'SLEEPING');
      if (isActive) markiereMeldungFaellig();
      else void meldeSchlaf(clientId).catch(() => undefined);
    }).catch(() => null);
    return () => {
      stoppClassic();
      stoppRelay();
      void resume.then((handle) => handle?.remove());
    };
  }, [clientId]);

  /**
   * Solange bewertet wird, darf iOS das iPad nicht sperren.
   *
   * ⚠️ **Das ist nicht die Schlafscheibe.** Die dunkelt unsere eigene Oberfläche ab und ist mit
   * einem Tipp wieder weg; iOS' eigene Frist (werkseitig zwei Minuten) hält sie nicht auf. Und
   * genau dann rührt der Prüfer nichts an: er sieht dem Prüfling zu. Danach stünde er vor dem
   * Sperrbildschirm, während die Untersuchung vor ihm endet.
   *
   * ⚠️ **Nur `bereit`, nicht die ganze Laufzeit.** In der Auswahl, beim Wiedereinstieg und
   * zwischen zwei Prüflingen soll das Gerät ruhig einschlafen — sonst leuchtet es auch durch
   * die Mittagspause, und auf einem iPad 6 ist das ein spürbarer Teil der Ladung.
   */
  useEffect(() => {
    void halteWach(startup.kind === 'bereit');
    return () => {
      void halteWach(false);
    };
  }, [startup.kind]);

  /**
   * Verlässt der Prüfer die App, fällt die Sperre vor den Einstellungen wieder zu.
   *
   * ⚠️ **`visibilitychange`, nicht `blur`.** Im WKWebView feuert `blur` auch dann, wenn nur
   * die Bildschirmtastatur aufgeht oder ein nativer Dialog (Kamera, Fotos) die Oberfläche
   * überdeckt — der Prüfer stünde mitten in der Arbeit wieder vor der Tür.
   *
   * Beim Einschlafen geschieht dasselbe, s. `schlaf/Schlafscheibe.tsx`.
   */
  useEffect(() => {
    const zu = () => {
      if (document.visibilityState === 'hidden') {
        useAppStore.getState().setEinstellungenFrei(false);
      }
    };
    document.addEventListener('visibilitychange', zu);
    return () => document.removeEventListener('visibilitychange', zu);
  }, []);

  /**
   * Der Weg nach draussen — **nur am Rechner** (`platform/beenden.ts`).
   *
   * ⚠️ **Der Hörer hängt hier und nicht im Blatt.** Die Hülle hält bei ⌘Q, beim Schliessknopf
   * und beim Menüeintrag an und fragt nach; wäre der Hörer erst da, wenn die Abfrage offen
   * ist, käme sie nie auf. Am iPad und im Browser gibt es nichts zu hören — dort gibt
   * `aufBeendenwunsch` eine leere Abbestellung zurück.
   */
  useEffect(() => {
    return aufBeendenwunsch(() => useAppStore.getState().setBeendenOffen(true));
  }, []);

  /**
   * Das Menü der Hülle spricht die Sprache der Oberfläche.
   *
   * ⚠️ **Hängt an `t`**, damit ein Sprachwechsel in den Einstellungen auch oben ankommt —
   * sonst stünde nach dem Umschalten auf Französisch weiter „Prüfung beenden" im Menü.
   */
  useEffect(() => {
    void beschrifteMenue({
      beenden: t.exit.menu,
      datei: t.exit.menuFile,
      bearbeiten: t.exit.menuEdit,
      ansicht: t.exit.menuView,
    });
  }, [t]);

  /**
   * Das Gerät hat sein Erscheinungsbild gewechselt.
   *
   * ⚠️ **Nur bei der Wahl „wie das iPad"** — das entscheidet der Store (`geraetIstDunkel`).
   * Wer „Hell" oder „Dunkel" gewählt hat, soll nicht mitten in einer Prüfung umspringen, weil
   * auf dem iPad die Nacht anbricht oder jemand am Kontrollzentrum zieht.
   */
  useEffect(() => {
    const abfrage = window.matchMedia('(prefers-color-scheme: dark)');
    const melde = () => useAppStore.getState().geraetIstDunkel(abfrage.matches);
    melde();
    abfrage.addEventListener('change', melde);
    return () => abfrage.removeEventListener('change', melde);
  }, []);

  // Ein Ort, an dem Farben entstehen: alles Weitere liest nur noch CSS-Variablen.
  // `data-theme` kommt dazu, damit Stylesheets die wenigen Fälle abbilden können, die
  // nicht über die Token laufen (die eigenen Farben der Setup-Bereiche etwa).
  useEffect(() => {
    applyTheme(document.documentElement, buildTheme(accent, dark));
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
  }, [accent, dark]);

  // Vor der Wahl gibt es nichts anzuzeigen: kein Kopf, kein Bogen, nur das Blatt auf leerem
  // Grund. Die Kopfzeile bräuchte eine Station, und die ist ja gerade das, was fehlt.
  if (startup.kind === 'wiedereinstieg') {
    return (
      <div className="stage stage-leer">
        <ResumeDialog />
        <ClockWarning />
        <AufsichtMitteilung />
        <Schlafscheibe />
        <BeendenDialog />
        <Toast />
      </div>
    );
  }
  if (startup.kind === 'auswahl') {
    return (
      <div className="stage stage-leer">
        {/* Das Zahnrad der Auswahl legt die Einstellungen **an ihre Stelle**, nicht darüber:
            zwei Blätter übereinander wären in dieser Phase der einzige Bildschirminhalt und
            sähen aus wie ein Fehler. `closeSheet` im Einstellungsblatt bringt die Auswahl
            zurück. Ohne diesen Zweig wäre ein Gerät ohne Prüfung eine Sackgasse — s. den
            Kommentar am Zahnrad in `SetupSheet.tsx`. */}
        {sheet.kind === 'settings' ? <Einstellungen /> : <SetupSheet />}
        <ClockWarning />
        <AufsichtMitteilung />
        <Schlafscheibe />
        <BeendenDialog />
        <Toast />
      </div>
    );
  }

  return (
    <div className="stage">
      <Header />
      {/* ⛔ **Der stehende Hinweis einer Simulationsprüfung** (Vorgabe des Nutzers,
          2026-09-18): „während der Prüfung dies ständig als Warnung gezeigt werden". Er steht
          hier zwischen Kopfzeile und Bogen und nicht in der Fusszeile — die trägt Absenden,
          Stationskommentar und Schlafknopf. Bei einer echten Prüfung zeichnet er nichts. */}
      <SimulativBand />
      {/* Der Bogen füllt die Mitte allein: die Fallvignette steht im Dialog darüber, nie
          daneben (Entscheidung des Nutzers, 2026-09-16). `Bogen` bringt `flex:1`/`min-height:0`
          selbst mit und braucht dafür keinen Zwischenkasten. */}
      <Bogen />
      <BogenFooter />
      <ActiveSheet />
      {/* Der Bogen steht schon da, aber es hat noch niemand davor Platz genommen. Das Blatt
          weicht dem Scanner, damit dessen Kamera nicht hinter einem Blatt läge; bricht der
          Prüfer dort ab, steht es wieder da. */}
      {startup.kind === 'pruefling' && sheet.kind === 'none' && <CandidatePickDialog />}
      {/* Liegt über der Prüflingsliste, deshalb außerhalb der Auswahl oben. */}
      {switchTarget && <SwitchDialog />}
      {/* Über dem Bogen, aber unter den Blättern: sie sperrt nur während des Abzugs. */}
      <CaptureOverlay />
      <ClockWarning />
      {/* Die Mitteilung der Prüfungsleitung kann in jeder Phase kommen (`AufsichtMitteilung`). */}
      <AufsichtMitteilung />
      {/* Die Scheibe gilt in jeder Phase und liegt über allem — auch über der Sperrscheibe
          des Bogenabzugs; dass sie während eines Abzugs nicht *entsteht*, regelt die Uhr
          selbst (`sollPausieren`).

          ⚠️ `imFuss` **nur bei geschlossenem Blatt**: dann sitzt der Schlafknopf in der
          Fusszeile des Bogens. Ist ein Blatt offen, liegt die Fusszeile darunter und nimmt
          keine Berührung mehr an (Radix setzt `pointer-events: none` auf den Body) — dort
          schwebt der Knopf weiter, sonst käme man im offenen Blatt nicht mehr ins Dunkle. */}
      <Schlafscheibe imFuss={sheet.kind === 'none'} />
      {/* Die Tür nach draussen liegt über allem, was der Prüfer sonst offen hat: ⌘Q kommt,
          wann es kommt, und der Bogen liegt dann vielleicht gerade unter den Einstellungen.
          Sie zeichnet nichts, solange niemand beenden will — und am iPad nie. */}
      <BeendenDialog />
      {/* Ganz zuletzt: die Einblendung liegt über allem anderen, auch über offenen Blättern. */}
      <Toast />
    </div>
  );
}

/** Höchstens ein Blatt liegt über dem Bogen; welches, entscheidet der Store. */
function ActiveSheet() {
  const sheet = useAppStore((s) => s.sheet);
  const tasks = useAppStore(selectTasks);
  const station = useAppStore(selectStation);
  const startup = useAppStore((s) => s.startup);

  // ⚠️ Ausdrückliche Schranke, keine beiläufige. Vier Blätter — Prüflingsliste, Wechsel,
  // Sperre, Absenden — rufen `requireCandidate`, und das **wirft**, solange keiner gewählt
  // ist. Heute erreicht sie in der Phase `pruefling` niemand, aber nur weil der Startdialog
  // modal davorliegt; eine Änderung an dessen Modalität liesse sie sonst werfen, und React
  // reisst bei einem Wurf im Rendern den ganzen Baum ab. Der Scanner ist ausgenommen: er ist
  // in dieser Phase genau der Weg zum Prüfling.
  if (startup.kind !== 'bereit' && sheet.kind !== 'scanner' && sheet.kind !== 'settings') {
    return null;
  }

  switch (sheet.kind) {
    case 'setup':
      return <SetupSheet />;
    case 'settings':
      return <Einstellungen />;
    case 'candidates':
      return <CandidatesSheet />;
    case 'vignette':
      return <VignetteDialog />;
    case 'timer':
      return <TimerDialog />;
    case 'comment': {
      const task = tasks.find((t) => t.id === sheet.taskId);
      // `key` erzwingt, was `CommentSheet` ohnehin voraussetzt: bei einem Wechsel der Aufgabe
      // muss das Blatt neu montieren, damit `geöffnetMit.current` (der Ausgangsstand der
      // Handschrift) auf die *jetzt* offene Aufgabe zeigt. Heute folgt das schon daraus, dass
      // zwischen zwei Öffnungen `null` gerendert wird.
      //
      // ⚠️ Das `key` macht den Vergleich richtig, **rettet aber nichts**: der Weg, auf dem eine
      // ungesicherte Handschrift in die Ablage kommt, liegt in `handleClose` und nicht in einem
      // Aufräumschritt beim Abbau. Entstünde später ein Aufgabenwechsel im *offenen* Blatt,
      // verwürfe das Neumontieren die noch nicht gesicherte Handschrift der alten Aufgabe.
      // Dann braucht es dort einen Aufräumschritt — mit diesem `key` allein ist es nicht getan.
      return task ? <CommentSheet key={task.id} task={task} /> : null;
    }
    case 'stationComment': {
      /*
       * Dasselbe Blatt wie beim Aufgabenkommentar, nur mit der **Station** als Ziel. Sie wird
       * dafür in die Form einer Aufgabe gebracht: der Kasten im Bogen steht unter ihrer
       * Kennung, und `CommentSheet` braucht von einer Aufgabe nur Kennung und Kurzname.
       *
       * ⚠️ `bezug` entscheidet, wohin Aufnahmen geschrieben werden (`logs/station-comment/…`)
       * — ohne ihn lägen sie bei den Aufgabenkommentaren und kämen dort nie wieder hervor.
       */
      const alsAufgabe = {
        id: station.id,
        title: station.label,
        short: station.label,
        typeLabel: '',
        scoring: { kind: 'global' as const, max: 0, body: '' },
      };
      return <CommentSheet key={station.id} task={alsAufgabe} bezug="station-comment" />;
    }
    case 'submit':
      return <SubmitDialog />;
    case 'locked':
      return <LockedDialog />;
    case 'scanner':
      return <ScannerScreen />;
    case 'score': {
      const task = tasks.find((t) => t.id === sheet.taskId);
      return task ? <ScoreDialog task={task} /> : null;
    }
    case 'criterion': {
      const task = tasks.find((t) => t.id === sheet.taskId);
      return task ? <CriterionDialog task={task} index={sheet.index} /> : null;
    }
    case 'none':
      return null;
  }
}

/**
 * Die Einstellungen — oder die Tür davor.
 *
 * Steht in den iPad-Einstellungen ein Passwort, geht der Dialog erst nach Eingabe auf
 * (`sheets/einstellungenSperre.ts`). Die Entscheidung liegt **hier**, an einer Stelle: die
 * Einstellungen gehen an zwei Orten auf — in der Auswahl-Phase und aus dem Bogen —, und eine
 * Tür, die nur an einem davon hängt, ist keine.
 *
 * ⚠️ **Der Helligkeitsregler steht vor der Tür**, nicht dahinter (Entscheidung des Nutzers,
 * 2026-09-17) — aber **nicht hier**. Er steht in beiden Blättern, je als erste Zeile
 * (`HelligkeitZeile`). Erster Versuch war eine gemeinsame Zeile über dieser Verzweigung; im
 * Browser lag sie unbedienbar am Seitenfuss hinter dem Dunkelfeld, denn Radix setzt bei
 * offenem Dialog `pointer-events: none` auf den Body. Zwei Aufrufe eines Bauteils sind
 * ehrlicher als ein Regler, den niemand greifen kann.
 *
 * Diese Verzweigung entscheidet also weiter nur das eine: ob gesperrt ist.
 */
function Einstellungen() {
  const passwort = useAppStore((s) => s.einstellungenPasswort);
  const frei = useAppStore((s) => s.einstellungenFrei);
  // Geschlossen heisst wieder gesperrt (Entscheidung des Nutzers, 2026-10-02) — gleich, auf
  // welchem Weg der Dialog zugeht. Vorher sperrten nur Hintergrund und Schlaf.
  useEffect(() => () => useAppStore.getState().setEinstellungenFrei(false), []);
  return istGesperrt({ passwort, frei }) ? <SperrDialog /> : <SettingsDialog />;
}
